// hooks/useMaxReadPosition.ts
//
// Tracks the *furthest* point the user has progressed in the current book and
// formats it as the free-form `max_read_position` string the text-chat backend
// pastes into the AI prompt for spoiler-safe answers.
//
// The client otherwise only tracks the *current* position, which moves backward
// when the user scrubs back. Here we keep a monotonic "max-ever" value instead.
// Local-only for now (resets on book change / reload); persistence can follow.

import { useEffect, useRef, useCallback } from 'react';
import { ReaderChapter } from '@/services/booksApi';

interface UseMaxReadPositionParams {
  bookId: string | undefined;
  /** Seconds elapsed across the whole book (prior chapters + current). */
  totalCurrentTime: number;
  totalBookDuration: number;
  currentChapter: ReaderChapter | null;
  getCurrentSentenceIndex: () => number;
}

interface MaxReadSnapshot {
  chapterTitle: string;
  sentenceIndex: number;
  totalProgress: number;
}

export function useMaxReadPosition({
  bookId,
  totalCurrentTime,
  totalBookDuration,
  currentChapter,
  getCurrentSentenceIndex,
}: UseMaxReadPositionParams) {
  const maxProgressRef = useRef<number>(0);
  const snapshotRef = useRef<MaxReadSnapshot | null>(null);

  // Reset the high-water mark when the book changes.
  useEffect(() => {
    maxProgressRef.current = 0;
    snapshotRef.current = null;
  }, [bookId]);

  // Advance the high-water mark whenever playback reaches further than ever.
  useEffect(() => {
    if (totalCurrentTime > maxProgressRef.current) {
      maxProgressRef.current = totalCurrentTime;
      snapshotRef.current = {
        chapterTitle: currentChapter?.title || '',
        sentenceIndex: getCurrentSentenceIndex(),
        totalProgress: totalCurrentTime,
      };
    }
  }, [totalCurrentTime, currentChapter, getCurrentSentenceIndex]);

  /**
   * Build the human-readable spoiler-safety note, or null if we have no
   * progress to report yet. The backend pastes this verbatim, so phrasing is
   * up to us.
   */
  const formatMaxRead = useCallback((): string | null => {
    const snap = snapshotRef.current;
    if (!snap || snap.totalProgress <= 0) {
      return null;
    }
    const percent =
      totalBookDuration > 0
        ? Math.min(100, Math.round((snap.totalProgress / totalBookDuration) * 100))
        : null;

    const chapterPart = snap.chapterTitle ? `Chapter "${snap.chapterTitle}", ` : '';
    const sentencePart = snap.sentenceIndex >= 0 ? `sentence ${snap.sentenceIndex}` : 'the start';
    const percentPart = percent !== null ? ` (~${percent}% of the book)` : '';
    return `The user has so far listened through ${chapterPart}${sentencePart}${percentPart}.`;
  }, [totalBookDuration]);

  return { formatMaxRead };
}
